import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const CountUpAnimation: React.FC<{
  end: number;
  duration?: number;
  suffix?: string;
  isVisible: boolean;
}> = ({ end, duration = 2000, suffix = '', isVisible }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    // Fallback: démarrer l'animation après 500ms même si pas visible
    const fallbackTimer = setTimeout(() => {
      if (!hasAnimated) {
        setHasAnimated(true);
        startAnimation();
      }
    }, 500);

    if (isVisible && !hasAnimated) {
      clearTimeout(fallbackTimer);
      setHasAnimated(true);
      startAnimation();
    }

    function startAnimation() {
      let startTime: number;
      let animationFrame: number;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = Math.floor(easeOutQuart * end);
        setCount(currentCount);

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end); // S'assurer que la valeur finale est exacte
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }

    return () => {
      clearTimeout(fallbackTimer);
    };
  }, [isVisible, hasAnimated, end, duration]);

  return <span>{count}{suffix}</span>;
};

const Hero: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Démarrer immédiatement l'animation après un court délai
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Seuil plus bas pour déclencher plus tôt
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);
  return (
    <section id="accueil" className="pt-20 bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Contenu principal */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                Institut Supérieur de
                <span className="text-blue-600 dark:text-blue-400 block">Technologie Médicale</span>
                <span className="text-2xl md:text-3xl lg:text-4xl text-gray-600 dark:text-gray-300 block mt-2">
                  du Cap-Haïtien
                </span>
              </h1>
              <div className="mt-4 text-xl text-blue-600 dark:text-blue-400 font-semibold">
                « ISTMECH »
              </div>
            </div>

            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              Former des technologistes médicaux compétents et éthiques pour répondre aux besoins
              de santé de la population haïtienne et contribuer au développement du système de santé national.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/formations#informations-generales"
                className="bg-blue-600 dark:bg-blue-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200 shadow-lg text-center"
              >
                Découvrir nos Formations
              </Link>
              <Link
                to="/contact#contact"
                className="border-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors duration-200 text-center"
              >
                Nous Contacter
              </Link>
            </div>

            {/* Statistiques */}
            <div ref={statsRef} className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200 dark:border-gray-700">
              <div className="text-center transform hover:scale-105 transition-transform duration-300">
                <div className="text-2xl font-bold text-blue-600">
                  <CountUpAnimation end={25} isVisible={isVisible} suffix="+" />
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Années d'Excellence</div>
              </div>
              <div className="text-center transform hover:scale-105 transition-transform duration-300">
                <div className="text-2xl font-bold text-blue-600">
                  <CountUpAnimation end={500} isVisible={isVisible} suffix="+" duration={2500} />
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Diplômés</div>
              </div>
              <div className="text-center transform hover:scale-105 transition-transform duration-300">
                <div className="text-2xl font-bold text-blue-600">
                  <CountUpAnimation end={15} isVisible={isVisible} suffix="+" duration={1800} />
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Professeurs</div>
              </div>
            </div>
          </div>

          {/* Image/Illustration */}
          <div className="relative">
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-8 text-center">
              {/* Placeholder pour image de l'institut */}
              <div className="space-y-6">
                <div className="w-32 h-32 bg-blue-600 dark:bg-blue-500 rounded-full mx-auto flex items-center justify-center">
                  <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">Notre Campus</h2>
                  <p className="text-blue-700 dark:text-blue-200 font-medium">Hôpital Universitaire Justinien</p>
                  <p className="text-blue-600 dark:text-blue-300">Rue 17 Q, Cap-Haïtien</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Situé au cœur du complexe hospitalier universitaire,
                    notre institut bénéficie d'un environnement d'apprentissage exceptionnel.
                  </p>
                </div>
              </div>
            </div>

            {/* Éléments décoratifs */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-green-400 rounded-full opacity-20"></div>
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue-400 rounded-full opacity-20"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
